import { captureException } from "@sentry/nextjs"
import { get } from "lodash"
// TODO imports from main code into module code
import router from "next/router"

import { platformIsInTestEnvironment } from "@basics/platform"
import { isFirstOccurrenceOfValueInArray } from "@basics/util-importless"
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "@locales/locales-config"
import { SUPPORTED_LOCALES_TYPES } from "@locales/locales-types"
import { DynamicTranslate, Ii18NKey, NamespaceShortcut } from "@services/i18n"
import { JsonEntriesType } from "@services/JSONHelper"

import { IChapter, CURRENT_CHAPTER_TEMPLATE_TYPE, IChapterTemplateDto } from "./chapter"
import { DocumentCategory, IDocument } from "./documents"


// #region document templates

/**
 * List of all known Document template categories.
 * The names should contain the type of the document as well as
 * Version number or similar, to handle versions.
 */
const DocumentTemplateCategories = [
  "businessplanV1",
  "sinnImpactCanvas",
  "sinnProjectCanvas",
] as const

/**
 * Type of all known document template categories.
 */
export type DocumentTemplateCategory = typeof DocumentTemplateCategories[number]

/**
 * Structure of the list of all known DocumentTemplateCategories and their link to the
 * document category and the template data within the I18n translations.
 */
type DocumentTemplateListStructure = {
  [key in DocumentTemplateCategory]: {
    /**
     * Category of the document, for which this template may be used.
     * There may be several templates for the same document category.
     */
    category: DocumentCategory
    /**
     * The i18nKey element should contain the namespace and the key, where the document
     * template data is located within the translation catalogue.
     * So it is possible to decouple the template name from its location in the i18n files.
     */
    i18nNode: Ii18NKey
  }
}

// #endregion known document types


// #region document template structures

/**
 * Inner structure of a document template
 */
export interface IDocumentTemplate {
  /**
   * Name/title of the template.
   */
  templateTitle: DocumentTemplateCategory
  /**
   * Title that is recommended to the user for naming his document.
   */
  recommendedTitle: string
  /**
   * List of chapter templates.
   */
  chapters: IChapterTemplate[]

  // TODO: recommended Attachments: TeamUpload/backendseitig definierte Dokumenttypen (z.B. Pitchdeck),
  // DataSet (Finanzplan)
}

/**
 * Structure of a single content entry of a checklist entry of a chapter template.
 */
interface ITemplateCheckListContentItem {
  /**
   * Title of the content element.
   */
  title: string
  /**
   * Describing/explaining content for a checklist entry.
   * May contain HTML
   */
  content: string
}

/**
 * Structure of a single checklist entry within a chapter template.
 */
export interface ITemplateChecklistEntry {
  /**
   * Key, with which this entry is put into the i18n file, e.g. "7"
   */
  key: string
  /**
   * The title of a checklist entry, that may be checked/unchecked.
   */
  title: string
  /**
   * A list of content items, that explain a checklist entry.
   */
  entries: ITemplateCheckListContentItem[]
}

/**
 * Structure of a single example entry within a chapter template.
 */
interface ITemplateExampleEntry {
  /**
   * The example.
   * May contain HTML.
   */
  example: string
  /**
   * Key, with which this entry is put into the i18n file, e.g. "greenTech"
   */
  key: string
  /**
   * The title of an example entry.
   */
  title: string
}

/**
 * Structure of a Chapter template
 */
interface IChapterTemplate {
  /**
   * Name of the chapter type.
   * Should be unique within a document template.
   */
  type: string
  /**
   * Recommended title of a chapter.
   */
  title: string
  /**
   * Explaining description of the chapter.
   */
  description: string
  /**
   * A list of checklist entries.
   */
  checklist: ITemplateChecklistEntry[]
  /**
   * Examples of a chapter content.
   */
  example: ITemplateExampleEntry[]
}

// #endregion document template structures


// #region DocumentTemplates class


/**
 * Collection of data and functions to handle Documents.
 */
export class DocumentTemplateHelper {

  // reserved names of keys within the translation file
  public static TITLE = "title"
  public static TEMPLATE_TITLE = "templateTitle"
  public static RECOMMENDED_TITLE = "recommendedTitle"
  public static DESCRIPTION = "description"
  public static CHECKLIST = "checklist"
  public static CONTENT = "content"
  public static EXAMPLES = "examples"

  /**
   * List of all known Document templates.
   * Must be extended on new templates.
   *
   * NOTE: beware of removing old templates! They will no longer be available for users
   * even if their document data in the database points to such a template!
   */
  public static documentTemplateList: DocumentTemplateListStructure = {
    "businessplanV1": {
      category: DocumentCategory.Businessplan,
      i18nNode: {
        namespace: "businessplan",
        key: "basicVersion"
      }
    },
    "sinnImpactCanvas": {
      category: DocumentCategory.Canvas,
      i18nNode: {
        namespace: "canvas",
        key: "sinnImpactCanvas"
      }
    },
    "sinnProjectCanvas": {
      category: DocumentCategory.Canvas,
      i18nNode: {
        namespace: "canvas",
        key: "sinnProjectCanvas"
      }
    }
  }

  /**
   * an array of the names of all known/registered template types
   */
  public static allKnownDocumentTemplateNames: DocumentTemplateCategory[] = [...DocumentTemplateCategories]

  /**
   * List of all template namespaces.
   * Should be used when loading namespaces for a page/component that uses templates to ensure
   * that all needed namespaces are loaded.
   * E.g.:
   * const usedNamespaces: NamespaceShortcut[] = DocumentTemplateHelper.namespacesOfAllTemplates
   * .concat(["default", "document-operations"])
   */
  public static namespacesOfAllTemplates: NamespaceShortcut[] =
    DocumentTemplateHelper.allKnownDocumentTemplateNames
      .map(name => DocumentTemplateHelper.documentTemplateList[name].i18nNode.namespace)
      // filter array to contain only 1 entry of every namespace
      .filter(isFirstOccurrenceOfValueInArray)

  /**
   * Storage to store already loaded DocumentTemplates
   *
   */
  public static loadedTemplates: Record<SUPPORTED_LOCALES_TYPES, Record<string, IDocumentTemplate>> = {}


  /**
   * Checks, if a given category is a known DocumentCategory.
   *
   * @param documentCategory category to be checked
   */
  public static isKnownDocumentCategory(documentCategory: string): documentCategory is DocumentCategory {
    return Object.values(DocumentCategory).includes(documentCategory as DocumentCategory)
  }

  /**
   * Checks, if the given template name matches to the given documentCategory
   * to avoid connecting a template to a wrong category.
   * null/undefined/"" is accepted as match, to allow documents to have no template.
   *
   * @param documentCategory document type that should be used
   * @param documentTemplate template, that should match to the documentTemplate
   */
  public static templateMatchesCategory(documentCategory: DocumentCategory, documentTemplate: DocumentTemplateCategory): boolean {
    const template = DocumentTemplateHelper.documentTemplateList[documentTemplate]

    // unset documentTemplate matches to all documentCategories
    return (!!template && template.category === documentCategory) || !documentTemplate
  }

  /**
   * @returns an array of the names of all known/registered template types
   */
  public static getAllKnownDocumentTemplateNames(): DocumentTemplateCategory[] {
    return [...DocumentTemplateCategories]
  }

  /**
   * Checks, if a given template name is known/valid.
   *
   * @param templateName name of a template
   * @returns true, if the given template name is known
   */
  public static isKnownTemplateName(templateName: string): boolean {
    return DocumentTemplateHelper.getAllKnownDocumentTemplateNames().includes(templateName as DocumentTemplateCategory)
  }

  /**
   * Filters all known templates to match for the given documentCategory.
   *
   * @param documentCategory documentCategory for which available templates are searched
   * @returns a list of all DocumentTemplate names, that are useable for the given documentCategory
   */
  public static getTemplateNamesForDocumentCategory(documentCategory: DocumentCategory): DocumentTemplateCategory[] {
    if (!documentCategory) {
      return []
    }

    return DocumentTemplateCategories.filter(templateName =>
      DocumentTemplateHelper.documentTemplateList[templateName].category === documentCategory
    )
  }

  /**
   * Returns an IDocumentTemplate from the given document template category, loaded from the translation file.
   *
   * The structure of the object is tested within tests.
   *
   * @param documentTemplateCategory name of the document template category
   * @param t a dynamic Translate function, to load the translation entries from the translation file
   */
  public static getDocumentTemplate(documentTemplateCategory: DocumentTemplateCategory, t: DynamicTranslate, locale: SUPPORTED_LOCALES_TYPES): IDocumentTemplate {
    if (!documentTemplateCategory) {
      return
    }

    if (!SUPPORTED_LOCALES.includes(locale)) {
      locale = DEFAULT_LOCALE
    }

    // initialising the template storage for the specific locale
    // if it was'nt initialized before
    if (!DocumentTemplateHelper.loadedTemplates[locale]) {
      DocumentTemplateHelper.loadedTemplates[locale] = {}
    }

    // maybe the searched document template was already loaded:
    // return it, otherwise load it
    if (DocumentTemplateHelper.loadedTemplates[locale]?.[documentTemplateCategory]) {
      return DocumentTemplateHelper.loadedTemplates[locale][documentTemplateCategory]
    }

    const documentTemplateMetaData = DocumentTemplateHelper.documentTemplateList[documentTemplateCategory]

    /**
     * Template data object from the document namespace and the given key.
     *
     * NOTE: if the namespace is not loaded, the data is not to be found.
     */
    const templateObject: JsonEntriesType = documentTemplateMetaData && get(
      t(documentTemplateMetaData.i18nNode.namespace, '.', {}, { returnObjects: true }),
      documentTemplateMetaData.i18nNode.key
    )

    if (!templateObject) {
      DocumentTemplateHelper.handleTemplateNotFound(documentTemplateCategory)
      return
    }

    // extract all chapter keys except reserved ones
    const chapterKeys = DocumentTemplateHelper.getChapterNames(templateObject)

    const template: IDocumentTemplate = {
      templateTitle: templateObject.templateTitle as DocumentTemplateCategory,
      recommendedTitle: templateObject.recommendedTitle as string,
      chapters: chapterKeys.map(key => DocumentTemplateHelper.transformChapterObject(
        key,
        templateObject[key] as JsonEntriesType
      ))
    }

    // store the loaded document into the local archive to speed up access next time
    DocumentTemplateHelper.loadedTemplates[locale][documentTemplateCategory] = template

    return template
  }

  // Create all the chapters, which will be the sent to the api in order to create
  // the chapters permanently.
  public static chaptersToCreateFromTemplate = (document: IDocument, t: DynamicTranslate): IChapter[] => {
    const chapters: IChapter[] = []
    const chosenTemplate = document.template && DocumentTemplateHelper.getDocumentTemplate(
      document.template,
      t,
      router.locale
    )
    Object.values(chosenTemplate.chapters).forEach(chapterTemplate => {
      const chapter: IChapter = {
        title: chapterTemplate.title,
        template: {
          "@type": CURRENT_CHAPTER_TEMPLATE_TYPE,
          chapter: chapterTemplate.type,
          document: document.template,
        },
      }
      chapters.push(chapter)
    })

    return chapters
  }

  /**
   * Extracts the chapter names for a (valid) template json object.
   * Ignores reserved keys within the template structure.
   *
   * @param entries a valid template json object
   * @returns the keys of the chapter names inside the json object
   */
  public static getChapterNames(entries: object): string[] {
    return Object
      .keys(entries)
      .filter(key => ![DocumentTemplateHelper.RECOMMENDED_TITLE, DocumentTemplateHelper.TEMPLATE_TITLE].includes(key))
  }

  /**
   * Returns the chapter with the searched type; or undefined, if the searched chapterType does not exist
   *
   * @param chapterTemplate name of the document tenplate + chapter template to be loaded
   * @param t a dynamic Translate function, to load the translation entries from the translation file
   * @param locale the current language of the users browser to make sure, templates are returned in the users language
   */
  public static getChapterTemplate(
    chapterTemplate: IChapterTemplateDto,
    /**
     * Required Namespaces: DocumentTemplateHelper.namespacesOfAllTemplates
     */
    t: DynamicTranslate,
    locale: SUPPORTED_LOCALES_TYPES
  ): IChapterTemplate | undefined {
    const documentTemplate = DocumentTemplateHelper.getDocumentTemplate(chapterTemplate?.document, t, locale)
    const foundChapter = documentTemplate?.chapters.find(chapter => chapter.type === chapterTemplate?.chapter) ?? undefined

    return foundChapter
  }

  /**
   * Handles errors on accessing template data, especially when template cannot be loaded,
   * because template namespace is not loaded by the using component.
   *
   * @param templateName name of the template type
   */
  public static handleTemplateNotFound(templateName: string): void {
    const errorText = `Template object not found: ${templateName}!\n
      Reason may be: Component missed importing template namespace dynamically!`

    // eslint-disable-next-line no-console
    console.error(
      errorText
      + (platformIsInTestEnvironment() ? "\nIn test situation: check mocking of useTranslation to load template file." : "")
    )

    if (!platformIsInTestEnvironment()) {
      const exception = new Error(errorText)
      captureException(exception)
    }
  }

  // #region helper functions

  /**
   * Transforms a checklist object from a translation file to a structured ChecklistEntry.
   *
   * Expected source data from a translation file is validated by tests.
   */
  protected static transformChecklistObject = (checklistObject: JsonEntriesType, entryKey: string): ITemplateChecklistEntry => {
    const entrySubKeys = Object.keys(checklistObject)
      // all checklist entries except the title
      .filter(key => key !== DocumentTemplateHelper.TITLE)
      .map(key => ({
        title: (checklistObject[key] as JsonEntriesType)[DocumentTemplateHelper.TITLE] as string,
        content: (checklistObject[key] as JsonEntriesType)[DocumentTemplateHelper.CONTENT] as string
      }))

    const template: ITemplateChecklistEntry = {
      key: entryKey,
      title: checklistObject[DocumentTemplateHelper.TITLE] as string,
      entries: entrySubKeys
    }

    return template
  }

  /**
   * Transforms a chapter object from a translation file to a structured ChapterTemplate.
   *
   * Expected source data from a translation file is validated by tests.
   */
  protected static transformChapterObject = (chapterType: string, chapterObject: JsonEntriesType): IChapterTemplate => {
    const checklistEntries = Object.keys(chapterObject[DocumentTemplateHelper.CHECKLIST])
      .map(key => DocumentTemplateHelper.transformChecklistObject((
        chapterObject[DocumentTemplateHelper.CHECKLIST] as JsonEntriesType)[key] as JsonEntriesType,
        key
      ))
    const exampleEntries = Object.keys(chapterObject[DocumentTemplateHelper.EXAMPLES])
      .map(key => {
        const exampleJson: JsonEntriesType = (chapterObject[DocumentTemplateHelper.EXAMPLES] as JsonEntriesType)[key] as JsonEntriesType
        return {
          key,
          title: exampleJson[DocumentTemplateHelper.TITLE] as string,
          example: exampleJson[DocumentTemplateHelper.CONTENT] as string
        }
      })

    const template: IChapterTemplate = {
      type: chapterType,
      title: chapterObject[DocumentTemplateHelper.TITLE] as string,
      description: chapterObject[DocumentTemplateHelper.DESCRIPTION] as string,
      checklist: checklistEntries,
      example: exampleEntries,
    }
    return template
  }

  // #endregion helper functions
}
// #endregion DocumentTemplates class